import React, { useEffect, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { DeleteOutlined, EditOutlined, LockOutlined, SettingOutlined, SyncOutlined, UnlockOutlined } from '@ant-design/icons';
import LoginStatusIndicator from '@components/common/elements/LoginStatusIndicator';
import KitplaySettingModal from '@components/common/form/KitplaySettingModal';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { DATE_FORMAT_VALUE, DEFAULT_TABLE_ITEM_SIZE, KITPLAY, STATUS_ACTIVE, TABLET_DEVICE_SERVICE_ORDER_CON } from '@constants';
import apiConfig from '@constants/apiConfig';
import { FieldTypes } from '@constants/formConfig';
import { deviceKinds, deviceStatus, expireKitplayOptions } from '@constants/masterData';
import useFetch from '@hooks/useFetch';
import useListBase from '@hooks/useListBase';
import useNotification from '@hooks/useNotification';
import useTranslate from '@hooks/useTranslate';
import { BaseTable, BaseTooltip } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import useDeviceFormModal from '@modules/listKitplayDevice/DeviceFormModal';
import routes from '@routes';
import { convertUtcToLocalTime } from '@utils';
import { setData } from '@utils/localStorage';
import { Button, Modal, Table } from 'antd';

import styles from '../kitplayShopAccount/index.module.scss';

const message = defineMessages({
    objectName: 'Kitplay Server device',
});

const ListOrderconPage = () => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const collapseId = params.get('collapseId');
    const [loadingSubItems, setLoadingSubItems] = useState({});
    const [fetchAt, setFetchedAt] = useState(null);
    const kinhValues = translate.formatKeys(deviceKinds, ['label']);

    const [searchParams, setSearchParams] = React.useState(null);
    const notification = useNotification();
    const { execute: approveDevice } = useFetch(apiConfig.kitplayDevice.approveDevice);
    const { execute: lockDevice } = useFetch(apiConfig.kitplayDevice.lockDevice);
    const [childData, setChildData] = useState({});
    const statusValues = translate.formatKeys(deviceStatus, ['label']);
    const expireValues = translate.formatKeys(expireKitplayOptions, ['label']);
    const [expandedRowKeys, setExpandedRowKeys] = useState(() => {
        return collapseId ? [(collapseId)] : [];
    });

    const [editingRowId, setEditingRowId] = useState(null);
    const [editingChildRowId, setEditingChildRowId] = useState(null);
    const [editingSettingId, setEditingSettingId] = useState(null);

    const [settingModalOpen, setSettingModalOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);

    const handleOpenSetting = (record) => {
        setSelectedRecord(record);
        setSettingModalOpen(true);
        setEditingSettingId(record.id);
    };

    const handleCloseSetting = () => {
        setSettingModalOpen(false);
        setSelectedRecord(null);
        setEditingSettingId(null);
    };

    const handleSubmit = (record) => {
        const dataUpdate = { id: record.id };
        const action = record.status === STATUS_ACTIVE ? lockDevice : approveDevice;
        action({
            data: dataUpdate,
            onCompleted: () => {
                const messageContent =
                    record.status === STATUS_ACTIVE
                        ? translate.formatMessage(commonMessage.lockSuccess)
                        : translate.formatMessage(commonMessage.approveSuccess);

                mixinFuncs.getList();
                notification({
                    message: messageContent,
                });
            },
            onError: (error) => {
                const messageContent =
                    record.status === STATUS_ACTIVE
                        ? translate.formatMessage(commonMessage.lockError)
                        : translate.formatMessage(commonMessage.approveError);

                notification({
                    message: messageContent,
                    type: 'error',
                });
            },
        });
    };

    const { execute: fetchSubItems } = useFetch(apiConfig.device.getList, {
        immediate: false,
        mappingData: (response) => {
            if (response.result === true) {
                return {
                    data: response.data.content,
                    total: response.data.totalElements,
                };
            } else {
                console.error('Error: Invalid response format', response);
            }
        },
    });

    const handleFetch = (id) => {
        setLoadingSubItems((prev) => ({
            ...prev,
            [id]: true,
        }));
        fetchSubItems({
            pathParams: { restaurantId: id },
            params: { service: TABLET_DEVICE_SERVICE_ORDER_CON },
            onCompleted: (response) => {
                if (response && response.data) {
                    if (response.data.content) {
                        const filteredData = response.data.content;
                        setChildData((prevData) => ({
                            ...prevData,
                            [id]: filteredData,
                        }));
                    } else {
                        setChildData((prevData) => ({
                            ...prevData,
                            [id]: [],
                        }));
                    }
                } else {
                    notification({
                        message: 'Error',
                        type: 'error',
                    });
                }
                setLoadingSubItems((prev) => ({
                    ...prev,
                    [id]: false,
                }));
            },
            onError: (error) => {
                const messageContent = 'Error';
                notification({
                    message: messageContent,
                    type: 'error',
                });
                setLoadingSubItems((prev) => ({
                    ...prev,
                    [id]: false,
                }));
            },
        });
    };

    useEffect(() => {
        if (collapseId) {
            handleFetch(collapseId);
            setExpandedRowKeys((prevKeys) => {
                if (!prevKeys.includes(collapseId)) {
                    return [...prevKeys, collapseId];
                }
                return prevKeys;
            });
        }
    }, [collapseId]);

    const { execute: executeDelete } = useFetch(apiConfig.kitplayDevice.delete);
    const { execute: executeDeletePrinter } = useFetch(apiConfig.printer.delete);
    const handleExpand = (expanded, record) => {
        if (expanded) {
            if (!childData[record.id]) {
                handleFetch(record.id);
            }
            setExpandedRowKeys((prevKeys) => [...prevKeys, record.id]);
        } else {
            setExpandedRowKeys((prevKeys) => prevKeys.filter((id) => id !== record.id));
        }
    };

    const { data, mixinFuncs, queryFilter, loading, pagination, changePagination } = useListBase({
        apiConfig: {
            ...apiConfig.kitplayDevice,
            getList: apiConfig.device.listRestaurant,
        },
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE,
            objectName: translate.formatMessage(message.objectName),
        },
        manual: true,
        override: (funcs) => {
            const prepareGetListParams = funcs.prepareGetListParams;
            funcs.prepareGetListParams = (params) => {
                return {
                    ...prepareGetListParams(params),
                    ...searchParams,
                    service: TABLET_DEVICE_SERVICE_ORDER_CON,
                };
            };
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    setFetchedAt(new Date());
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            funcs.showDeleteItemConfirm = (id, parent) => {
                Modal.confirm({
                    title: translate.formatMessage(commonMessage.deleteDeviceConfirm),
                    content: '',
                    okText: translate.formatMessage(commonMessage.yes),
                    cancelText: translate.formatMessage(commonMessage.no),
                    centered: true,
                    onOk: async () => {
                        await executeDelete({
                            pathParams: { id },
                            params: { service: KITPLAY },
                            onCompleted: (response) => {
                                mixinFuncs.onDeleteItemCompleted(id);
                                notification({
                                    message: translate.formatMessage(commonMessage.deleteDeviceSuccess),
                                });
                                if (parent) handleFetch(parent.id);
                            },
                            onError: (error) => {
                                mixinFuncs.handleDeleteItemError(error);
                            },
                        });
                    },
                });
            };
            funcs.getItemDetailLink = (dataRow) => {
                const currentPath = location.pathname;
                const searchParams = new URLSearchParams(location.search);
                searchParams.set('collapseId', dataRow.id);
                return `${currentPath}/${dataRow.id}?${searchParams.toString()}`;
            };

            funcs.getCreateLink = () => {
                const currentPath = location.pathname;
                const searchParams = location.search;
                return `${currentPath}/create${searchParams}`;
            };
            funcs.additionalActionColumnButtons = () => {
                const baseButtons = {
                    editMain: (record) => (
                        <BaseTooltip type="edit">
                            <Button
                                type="link"
                                style={{ padding: 0 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingRowId(record.id);
                                    openMainEdit(record);
                                }}
                            >
                                <EditOutlined />
                            </Button>
                        </BaseTooltip>
                    ),
                };
                if (!mixinFuncs.hasPermission([apiConfig.kitplayDevice.approveDevice.baseURL])) return baseButtons;
                return {
                    ...baseButtons,
                    active: (record) => {
                        const Icon = record.status === 1 ? LockOutlined : UnlockOutlined;
                        const iconStyle = record.status === 1 ? { color: 'red' } : {};
                        const title =
                            record.status === 1
                                ? translate.formatMessage(commonMessage.lockKitplayServer)
                                : translate.formatMessage(commonMessage.approveKitplayServer);
                        return (
                            <BaseTooltip title={title}>
                                <Button
                                    type="link"
                                    style={{ padding: 0 }}
                                    onClick={async () => {
                                        await handleSubmit(record);
                                        if (record.parent) {
                                            await handleFetch(record.parent.id);
                                        }
                                    }}
                                >
                                    <Icon style={iconStyle} />
                                </Button>
                            </BaseTooltip>
                        );
                    },
                    loginStatus: (record) => {
                        return <LoginStatusIndicator lastTimeLogin={record.lastTimeLogin} fetchedAt={fetchAt} translate={translate} />;
                    },
                    setting: (record) => {
                        return (
                            <BaseTooltip title={translate.formatMessage(commonMessage.editSetting)}>
                                <Button
                                    type="link"
                                    style={{ padding: 0 }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenSetting(record);
                                    }}
                                >
                                    <SettingOutlined />
                                </Button>
                            </BaseTooltip>
                        );
                    },
                    deleteDevice: (record) => (
                        <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={async (e) => {
                                e.stopPropagation();
                                Modal.confirm({
                                    title: translate.formatMessage(commonMessage.deletePrinterConfirm),
                                    content: '',
                                    okText: translate.formatMessage(commonMessage.yes),
                                    cancelText: translate.formatMessage(commonMessage.no),
                                    centered: true,
                                    onOk: async () => {
                                        await executeDeletePrinter({
                                            pathParams: { id: record.printer.id },
                                            onCompleted: () => {
                                                notification({
                                                    message: translate.formatMessage(commonMessage.deleteDeviceSuccess),
                                                });
                                                if (record.parent) handleFetch(record.parent.id);
                                            },
                                            onError: (error) => {
                                                mixinFuncs.handleDeleteItemError(error);
                                            },
                                        });
                                    },
                                });
                            }}
                        >
                            <DeleteOutlined style={{ color: 'red' }} />
                        </Button>
                    ),
                    editDevice: (record) => {
                        return (
                            <BaseTooltip type="edit">
                                <Button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingChildRowId(record.id);
                                        openChildEdit(record);
                                    }}
                                    type="link"
                                    style={{ padding: 0 }}
                                >
                                    <EditOutlined color="red" />
                                </Button>
                            </BaseTooltip>
                        );
                    },
                };
            };
        },
    });

    const { openMainEdit, openChildEdit, renderModals } = useDeviceFormModal({
        onReloadList: () => mixinFuncs.getList(),
        onReloadChild: (parentId) => handleFetch(parentId),
        onCloseMain: () => setEditingRowId(null),
        onCloseChild: () => setEditingChildRowId(null),
    });

    const expandedRowRender = (record) => {
        const childItems = childData[record.id] || [];
        const isLoading = loadingSubItems[record.id] || false;
        const columns = [
            { title: <FormattedMessage defaultMessage="Device Name" />, dataIndex: 'deviceName' },
            {
                title: <FormattedMessage defaultMessage="Device Token" />,
                dataIndex: ['deviceToken'],
            },
            {
                title: <FormattedMessage defaultMessage="Device Kind" />,
                dataIndex: ['deviceKind'],
                render(dataRow) {
                    const state = kinhValues.find((item) => item.value == dataRow);
                    return <div>{state?.label}</div>;
                },
                width: 200,
            },
            {
                title: <FormattedMessage defaultMessage="Expiration date" />,
                width: 180,
                dataIndex: 'expireDate',
                align: 'right',
                render: (createdDate) => {
                    const createdDateLocal = convertUtcToLocalTime(createdDate, DATE_FORMAT_VALUE, DATE_FORMAT_VALUE);
                    return <div>{createdDateLocal}</div>;
                },
            },
            mixinFuncs.renderStatusColumn({
                width: '120px',
                dataIndex: ['status'],
            }),
            // mixinFuncs.renderActionColumn(
            //     {
            //         // setting: { ignorePermission: true },
            //         // editDevice: mixinFuncs.hasPermission([apiConfig.printer.update.baseURL]),
            //         // deleteDevice: mixinFuncs.hasPermission([apiConfig.printer.delete.baseURL]),
            //         loginStatus: true,
            //     },
            //     { width: '120px', align: 'center' },
            // ),
        ];
        return (
            <>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        position: 'relative',
                        zIndex: 10,
                        marginBottom: 8,
                        padding: '0 30px',
                    }}
                >
                    <span style={{ fontWeight: 'bold', paddingLeft: 88 }}>
                        <FormattedMessage defaultMessage="Ordercon device" />
                    </span>
                    <SyncOutlined
                        style={{ color: '#1677ff', cursor: 'pointer' }}
                        onClick={() => handleFetch(record.id)}
                    />
                </div>
                <Table
                    className={styles['scroll-container']}
                    columns={columns}
                    dataSource={childItems}
                    pagination={false}
                    rowKey={(record) => record.id}
                    loading={isLoading}
                    rowClassName={(record) =>
                        record.id === editingChildRowId || record.id === editingSettingId
                            ? styles['highlighted-row']
                            : ''
                    }
                />
            </>
        );
    };

    const columns = [
        {
            title: <FormattedMessage defaultMessage="Restaurant name" />,
            dataIndex: ['restaurantName'],
            render: (text, record) => {
                return (
                    <div
                        className={styles.div}
                        style={{ cursor: 'pointer', color: '#4096ff' }}
                        onClick={(event) => {
                            event.stopPropagation();
                            const currentParams = new URLSearchParams(location.search);
                            currentParams.set('collapseId', record.id);
                            navigate(
                                `${generatePath(routes.listDigibesDevicePage.path, {
                                    restaurantId: record?.id,
                                })}?restaurantId=${record.id}&restaurantName=${record.restaurantName
                                }&${currentParams.toString()}`,
                            );
                        }}
                    >
                        {text}
                    </div>
                );
            },
            // width: 260,
        },
        {
            title: <FormattedMessage defaultMessage="Created date" />,
            width: 180,
            dataIndex: 'createdDate',
            align: 'right',
            render: (createdDate) => {
                const createdDateLocal = convertUtcToLocalTime(createdDate, DATE_FORMAT_VALUE, DATE_FORMAT_VALUE);
                return <div>{createdDateLocal}</div>;
            },
        },
        // {
        //     title: <FormattedMessage defaultMessage="Expiration date" />,
        //     width: 180,
        //     dataIndex: 'expireDate',
        //     align: 'right',
        //     render: (createdDate) => {
        //         const createdDateLocal = convertUtcToLocalTime(createdDate, DATE_FORMAT_VALUE, DATE_FORMAT_VALUE);
        //         return <div>{createdDateLocal}</div>;
        //     },
        // },
        mixinFuncs.renderStatusColumn({ width: '120px' }),
        // mixinFuncs.renderActionColumn(
        //     {
        //         editMain: mixinFuncs.hasPermission([apiConfig.kitplayDevice.update.baseURL]),
        //         delete: mixinFuncs.hasPermission([apiConfig.kitplayDevice.delete.baseURL]),
        //         loginStatus: true,
        //     },
        //     { width: '100px', className: styles['action-column'] },
        // ),
    ];

    const searchFields = [
        {
            key: 'restaurantName',
            placeholder: translate.formatMessage(commonMessage.restaurantName),
            colSpan: 4,
        },
        // {
        //     key: 'deviceId',
        //     placeholder: translate.formatMessage(commonMessage.deviceId),
        //     colSpan: 4,
        // },
        {
            key: 'restaurantStatus',
            placeholder: translate.formatMessage(commonMessage.status),
            type: FieldTypes.SELECT,
            options: statusValues,
            colSpan: 4,
        },
        {
            key: 'expireStatus',
            placeholder: translate.formatMessage(commonMessage.expireDate),
            type: FieldTypes.SELECT,
            options: expireValues,
            colSpan: 6,
        },
    ];

    return (
        <PageWrapper routes={[{ breadcrumbName: translate.formatMessage(commonMessage.ordercon) }]}>
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({
                    fields: searchFields,
                    initialValues: queryFilter,
                    isReload: true,
                })}
                baseTable={
                    <BaseTable
                        onChange={changePagination}
                        pagination={pagination}
                        loading={loading}
                        dataSource={data}
                        columns={columns}
                        expandable={{
                            expandedRowRender,
                            expandedRowKeys: expandedRowKeys,
                            defaultExpandedRowKeys: ['0'],
                            onExpand: handleExpand,
                        }}
                        rowKey="id"
                    />
                }
            />

            {renderModals()}
            <KitplaySettingModal
                open={settingModalOpen}
                record={selectedRecord}
                onClose={handleCloseSetting}
                onSuccess={() => {
                    mixinFuncs.getList();
                    if (selectedRecord && selectedRecord.parent && selectedRecord.parent.id) {
                        handleFetch(selectedRecord.parent.id);
                    }
                }}
            />
        </PageWrapper>
    );
};

export default ListOrderconPage;
